/**
 * CartoonCanvas Component
 *
 * Modern 3D cartoon miniature scene visualization.
 * Apple-inspired design with soft PBR-style lighting.
 * 1080x1080 square format with header overlay.
 */

import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import {
  CARTOON_CANVAS,
  CARTOON_COLORS,
  CARTOON_GRID,
  TIME_LIGHTING,
  ANIMATION_CONFIG,
  isoToScreen,
  calculateDepth,
  roundRect,
  applySoftShadow,
  clearShadow,
  calculateMarketSentiment,
  MarketSentiment,
  TimeLighting,
  getTimeOfDay,
} from './cartoon.config';
import { drawHeaderOverlay } from './HeaderOverlay';
import {
  IsoSimulationState,
  IsoAgent,
  IsoKPIDisplay,
} from '../../../types/isometric.types';

interface CartoonCanvasProps {
  state: IsoSimulationState;
  mundusName?: string;
  onAgentClick?: (agent: IsoAgent) => void;
  onKPIClick?: (kpi: IsoKPIDisplay) => void;
  className?: string;
}

export const CartoonCanvas: React.FC<CartoonCanvasProps> = ({
  state,
  mundusName,
  onAgentClick: _onAgentClick,
  onKPIClick: _onKPIClick,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();

  const { WIDTH, HEIGHT } = CARTOON_CANVAS;
  const { TILE_WIDTH, TILE_HEIGHT, GRID_COLS, GRID_ROWS, ORIGIN_X, ORIGIN_Y } = CARTOON_GRID;

  // Calculate market sentiment from KPIs
  const sentiment = useMemo<MarketSentiment>(() => {
    return calculateMarketSentiment(state.kpis);
  }, [state.kpis]);

  // Get current time lighting
  const timeLighting = useMemo<TimeLighting>(() => {
    const timeOfDay = getTimeOfDay(state.currentTime.getHours());
    return TIME_LIGHTING[timeOfDay];
  }, [state.currentTime]);

  // ===========================================
  // DRAWING FUNCTIONS
  // ===========================================

  // Draw sky/background gradient
  const drawBackground = useCallback((ctx: CanvasRenderingContext2D) => {
    const gradient = ctx.createLinearGradient(0, CARTOON_CANVAS.HEADER_HEIGHT, 0, HEIGHT);
    gradient.addColorStop(0, timeLighting.skyGradient[0]);
    gradient.addColorStop(1, timeLighting.skyGradient[1]);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
  }, [timeLighting, HEIGHT, WIDTH]);

  // Draw isometric floor tile with 3D effect
  const drawTile = useCallback((
    ctx: CanvasRenderingContext2D,
    col: number,
    row: number,
    isCarpet: boolean = false
  ) => {
    const { x, y } = isoToScreen(col, row, ORIGIN_X, ORIGIN_Y);
    const colors = isCarpet ? CARTOON_COLORS.building : CARTOON_COLORS.building;
    const mainColor = isCarpet ? colors.carpet : colors.floor;
    const patternColor = isCarpet ? colors.carpetPattern : colors.floorPattern;

    // Main tile shape
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + TILE_WIDTH / 2, y + TILE_HEIGHT / 2);
    ctx.lineTo(x, y + TILE_HEIGHT);
    ctx.lineTo(x - TILE_WIDTH / 2, y + TILE_HEIGHT / 2);
    ctx.closePath();

    // Fill with slight gradient for 3D effect
    const gradient = ctx.createLinearGradient(x - TILE_WIDTH / 2, y, x + TILE_WIDTH / 2, y + TILE_HEIGHT);
    gradient.addColorStop(0, mainColor);
    gradient.addColorStop(1, (col + row) % 2 === 0 ? patternColor : mainColor);
    ctx.fillStyle = gradient;
    ctx.fill();

    // Subtle border
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.05)';
    ctx.lineWidth = 0.5;
    ctx.stroke();
  }, [ORIGIN_X, ORIGIN_Y, TILE_WIDTH, TILE_HEIGHT]);

  // Draw 3D isometric box with soft shadows
  const drawIsoBox = useCallback((
    ctx: CanvasRenderingContext2D,
    col: number,
    row: number,
    width: number,
    depth: number,
    height: number,
    topColor: string,
    leftColor: string,
    rightColor: string,
    withShadow: boolean = true
  ) => {
    const { x, y } = isoToScreen(col, row, ORIGIN_X, ORIGIN_Y);
    const hw = (width * TILE_WIDTH) / 2;
    const hd = (depth * TILE_HEIGHT) / 2;

    // Soft shadow beneath
    if (withShadow) {
      applySoftShadow(ctx, 4, 4, 8, CARTOON_COLORS.shadows.soft);
    }

    // Top face
    ctx.beginPath();
    ctx.moveTo(x, y - height);
    ctx.lineTo(x + hw, y + hd / 2 - height);
    ctx.lineTo(x, y + hd - height);
    ctx.lineTo(x - hw, y + hd / 2 - height);
    ctx.closePath();
    ctx.fillStyle = topColor;
    ctx.fill();

    clearShadow(ctx);

    // Left face (darker)
    ctx.beginPath();
    ctx.moveTo(x - hw, y + hd / 2 - height);
    ctx.lineTo(x, y + hd - height);
    ctx.lineTo(x, y + hd);
    ctx.lineTo(x - hw, y + hd / 2);
    ctx.closePath();
    ctx.fillStyle = leftColor;
    ctx.fill();

    // Right face (medium)
    ctx.beginPath();
    ctx.moveTo(x + hw, y + hd / 2 - height);
    ctx.lineTo(x, y + hd - height);
    ctx.lineTo(x, y + hd);
    ctx.lineTo(x + hw, y + hd / 2);
    ctx.closePath();
    ctx.fillStyle = rightColor;
    ctx.fill();

    // Edge highlights
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x - hw, y + hd / 2 - height);
    ctx.lineTo(x, y - height);
    ctx.lineTo(x + hw, y + hd / 2 - height);
    ctx.stroke();
  }, [ORIGIN_X, ORIGIN_Y, TILE_WIDTH, TILE_HEIGHT]);

  // Draw modern desk with cartoon style
  const drawDesk = useCallback((ctx: CanvasRenderingContext2D, col: number, row: number) => {
    const { x, y } = isoToScreen(col, row, ORIGIN_X, ORIGIN_Y);
    const { furniture } = CARTOON_COLORS;

    // Desk surface
    drawIsoBox(ctx, col, row, 1.6, 1, 22, furniture.deskWoodLight, furniture.deskWood, furniture.deskWoodDark);

    // Monitor stand
    ctx.fillStyle = furniture.deskMetal;
    ctx.fillRect(x - 4, y - 35, 8, 12);

    // Monitor back
    applySoftShadow(ctx, 2, 2, 4, CARTOON_COLORS.shadows.soft);
    ctx.fillStyle = '#2D2D2D';
    roundRect(ctx, x - 16, y - 58, 32, 24, 3);
    ctx.fill();
    clearShadow(ctx);

    // Monitor screen
    ctx.fillStyle = '#4DA6FF';
    roundRect(ctx, x - 14, y - 56, 28, 20, 2);
    ctx.fill();

    // Screen reflection
    ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.fillRect(x - 12, y - 54, 8, 16);
  }, [drawIsoBox, ORIGIN_X, ORIGIN_Y]);

  // Draw office chair with cartoon style
  const drawChair = useCallback((ctx: CanvasRenderingContext2D, col: number, row: number) => {
    const { x, y } = isoToScreen(col, row, ORIGIN_X, ORIGIN_Y);
    const { furniture } = CARTOON_COLORS;

    // Shadow
    applySoftShadow(ctx, 3, 3, 6, CARTOON_COLORS.shadows.soft);

    // Base/wheels
    ctx.fillStyle = furniture.chair;
    ctx.beginPath();
    ctx.arc(x, y + 2, 10, 0, Math.PI * 2);
    ctx.fill();

    clearShadow(ctx);

    // Seat cushion
    const seatGradient = ctx.createRadialGradient(x - 4, y - 16, 0, x, y - 14, 16);
    seatGradient.addColorStop(0, furniture.chairSeatLight);
    seatGradient.addColorStop(1, furniture.chairSeat);
    ctx.fillStyle = seatGradient;
    ctx.beginPath();
    ctx.ellipse(x, y - 14, 15, 9, 0, 0, Math.PI * 2);
    ctx.fill();

    // Back rest
    const backGradient = ctx.createRadialGradient(x - 4, y - 38, 0, x, y - 35, 14);
    backGradient.addColorStop(0, furniture.chairSeatLight);
    backGradient.addColorStop(1, furniture.chairSeat);
    ctx.fillStyle = backGradient;
    ctx.beginPath();
    ctx.ellipse(x, y - 36, 13, 16, 0, Math.PI, Math.PI * 2);
    ctx.fill();

    // Highlight
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(x, y - 38, 10, Math.PI * 1.2, Math.PI * 1.8);
    ctx.stroke();
  }, [ORIGIN_X, ORIGIN_Y]);

  // Draw plant with cartoon style
  const drawPlant = useCallback((ctx: CanvasRenderingContext2D, col: number, row: number, isLarge: boolean) => {
    const { x, y } = isoToScreen(col, row, ORIGIN_X, ORIGIN_Y);
    const scale = isLarge ? 1 : 0.65;
    const { furniture } = CARTOON_COLORS;

    // Pot shadow
    applySoftShadow(ctx, 3, 3, 6, CARTOON_COLORS.shadows.soft);

    // Pot
    ctx.beginPath();
    ctx.moveTo(x - 16 * scale, y - 5);
    ctx.lineTo(x + 16 * scale, y - 5);
    ctx.lineTo(x + 12 * scale, y + 22 * scale);
    ctx.lineTo(x - 12 * scale, y + 22 * scale);
    ctx.closePath();
    const potGradient = ctx.createLinearGradient(x - 16 * scale, y, x + 16 * scale, y);
    potGradient.addColorStop(0, furniture.plantPotLight);
    potGradient.addColorStop(0.5, furniture.plantPot);
    potGradient.addColorStop(1, furniture.plantPot);
    ctx.fillStyle = potGradient;
    ctx.fill();

    clearShadow(ctx);

    // Foliage circles with gradient
    const foliagePositions = [
      { dx: 0, dy: -32 * scale, r: 20 * scale },
      { dx: -14 * scale, dy: -22 * scale, r: 14 * scale },
      { dx: 14 * scale, dy: -22 * scale, r: 14 * scale },
      { dx: -8 * scale, dy: -44 * scale, r: 12 * scale },
      { dx: 8 * scale, dy: -44 * scale, r: 12 * scale },
      { dx: 0, dy: -52 * scale, r: 10 * scale },
    ];

    foliagePositions.forEach(({ dx, dy, r }) => {
      const leafGradient = ctx.createRadialGradient(
        x + dx - r * 0.3, y + dy - r * 0.3, 0,
        x + dx, y + dy, r
      );
      leafGradient.addColorStop(0, furniture.plantLight);
      leafGradient.addColorStop(1, furniture.plant);
      ctx.fillStyle = leafGradient;
      ctx.beginPath();
      ctx.arc(x + dx, y + dy, r, 0, Math.PI * 2);
      ctx.fill();
    });
  }, [ORIGIN_X, ORIGIN_Y]);

  // Draw coffee machine with cartoon style
  const drawCoffeeMachine = useCallback((ctx: CanvasRenderingContext2D, col: number, row: number) => {
    const { x, y } = isoToScreen(col, row, ORIGIN_X, ORIGIN_Y);

    // Machine body
    drawIsoBox(ctx, col, row, 0.65, 0.45, 45, '#5A6270', '#4A5568', '#3A4150');

    // Water tank (glass effect)
    ctx.fillStyle = 'rgba(66, 153, 225, 0.4)';
    roundRect(ctx, x - 10, y - 60, 20, 25, 3);
    ctx.fill();

    // Glass reflection
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.fillRect(x - 8, y - 58, 6, 20);

    // Drip area
    ctx.fillStyle = '#718096';
    roundRect(ctx, x - 12, y - 12, 24, 8, 2);
    ctx.fill();

    // Coffee cup
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.arc(x, y - 5, 7, 0, Math.PI * 2);
    ctx.fill();

    // Coffee liquid
    ctx.fillStyle = '#6B4423';
    ctx.beginPath();
    ctx.arc(x, y - 5, 5, 0, Math.PI * 2);
    ctx.fill();

    // Steam particles (animated)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    for (let i = 0; i < 3; i++) {
      const steamY = y - 15 - i * 6 - (Date.now() / 200 % 10);
      const steamX = x + Math.sin(Date.now() / 300 + i) * 3;
      ctx.beginPath();
      ctx.arc(steamX, steamY, 2 - i * 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [drawIsoBox, ORIGIN_X, ORIGIN_Y]);

  // Draw meeting table
  const drawMeetingTable = useCallback((ctx: CanvasRenderingContext2D, col: number, row: number) => {
    drawIsoBox(ctx, col, row, 2.5, 1.5, 18, '#D4B896', '#C4A484', '#A08060');
  }, [drawIsoBox]);

  // Draw KPI screen (modern monitor style)
  const drawKPIScreen = useCallback((ctx: CanvasRenderingContext2D, col: number, row: number, kpis: IsoKPIDisplay[]) => {
    const { x, y } = isoToScreen(col, row, ORIGIN_X, ORIGIN_Y);
    const { screens } = CARTOON_COLORS;

    // Monitor stand
    ctx.fillStyle = screens.bezel;
    ctx.fillRect(x - 6, y - 25, 12, 28);

    // Monitor base
    ctx.fillStyle = screens.frame;
    ctx.beginPath();
    ctx.ellipse(x, y + 6, 28, 10, 0, 0, Math.PI * 2);
    ctx.fill();

    // Monitor frame with shadow
    applySoftShadow(ctx, 4, 4, 10, CARTOON_COLORS.shadows.medium);
    ctx.fillStyle = screens.frame;
    roundRect(ctx, x - 88, y - 148, 176, 125, 8);
    ctx.fill();
    clearShadow(ctx);

    // Screen bezel
    ctx.fillStyle = screens.frameDark;
    roundRect(ctx, x - 84, y - 144, 168, 117, 6);
    ctx.fill();

    // Screen
    ctx.fillStyle = screens.screen;
    roundRect(ctx, x - 80, y - 140, 160, 109, 4);
    ctx.fill();

    // Screen glass reflection
    const glassGradient = ctx.createLinearGradient(x - 80, y - 140, x + 80, y - 30);
    glassGradient.addColorStop(0, 'rgba(255,255,255,0.08)');
    glassGradient.addColorStop(0.5, 'rgba(255,255,255,0)');
    glassGradient.addColorStop(1, 'rgba(255,255,255,0.04)');
    ctx.fillStyle = glassGradient;
    roundRect(ctx, x - 80, y - 140, 160, 109, 4);
    ctx.fill();

    // Screen border glow
    ctx.strokeStyle = screens.kpi.accent;
    ctx.lineWidth = 2;
    roundRect(ctx, x - 80, y - 140, 160, 109, 4);
    ctx.stroke();

    // Header
    ctx.fillStyle = screens.kpi.accent;
    ctx.font = 'bold 12px "SF Pro Display", -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('REAL-TIME KPIs', x, y - 122);

    // KPI bars
    kpis.slice(0, 3).forEach((kpi, i) => {
      const kpiY = y - 100 + i * 32;

      // Label
      ctx.fillStyle = '#A0AEC0';
      ctx.font = '10px "SF Pro Text", -apple-system, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(kpi.name, x - 72, kpiY);

      // Progress bar background
      ctx.fillStyle = '#2D3748';
      roundRect(ctx, x - 72, kpiY + 5, 100, 12, 3);
      ctx.fill();

      // Progress bar fill
      const progress = Math.min(100, (kpi.value / kpi.target) * 100);
      const barColor = kpi.status === 'EXCELLENT' ? screens.kpi.excellent
        : kpi.status === 'GOOD' ? screens.kpi.good
        : kpi.status === 'WARNING' ? screens.kpi.warning
        : screens.kpi.critical;

      ctx.fillStyle = barColor;
      roundRect(ctx, x - 72, kpiY + 5, (progress / 100) * 100, 12, 3);
      ctx.fill();

      // Value
      ctx.fillStyle = '#F7FAFC';
      ctx.font = 'bold 10px "JetBrains Mono", monospace';
      ctx.textAlign = 'right';
      ctx.fillText(`${kpi.value}/${kpi.target}`, x + 72, kpiY + 14);
    });
  }, [ORIGIN_X, ORIGIN_Y]);

  // Draw Alarm screen
  const drawAlarmScreen = useCallback((ctx: CanvasRenderingContext2D, col: number, row: number, kpis: IsoKPIDisplay[], animFrame: number) => {
    const { x, y } = isoToScreen(col, row, ORIGIN_X, ORIGIN_Y);
    const { screens } = CARTOON_COLORS;
    const activeAlarms = kpis.filter(k => k.alarmActive);
    const hasAlarms = activeAlarms.length > 0;
    const pulse = hasAlarms ? (Math.sin(animFrame * 0.1) + 1) / 2 : 0;

    // Monitor stand
    ctx.fillStyle = screens.bezel;
    ctx.fillRect(x - 6, y - 25, 12, 28);

    // Monitor base
    ctx.fillStyle = screens.frame;
    ctx.beginPath();
    ctx.ellipse(x, y + 6, 28, 10, 0, 0, Math.PI * 2);
    ctx.fill();

    // Monitor frame
    applySoftShadow(ctx, 4, 4, 10, CARTOON_COLORS.shadows.medium);
    ctx.fillStyle = screens.frame;
    roundRect(ctx, x - 88, y - 148, 176, 125, 8);
    ctx.fill();
    clearShadow(ctx);

    // Screen bezel
    ctx.fillStyle = screens.frameDark;
    roundRect(ctx, x - 84, y - 144, 168, 117, 6);
    ctx.fill();

    // Screen
    ctx.fillStyle = hasAlarms ? '#1C1917' : screens.screen;
    roundRect(ctx, x - 80, y - 140, 160, 109, 4);
    ctx.fill();

    // Screen border glow (pulsing red if alarms)
    ctx.strokeStyle = hasAlarms
      ? `rgba(255, 59, 48, ${0.6 + pulse * 0.4})`
      : screens.alarm.safe;
    ctx.lineWidth = 3;
    roundRect(ctx, x - 80, y - 140, 160, 109, 4);
    ctx.stroke();

    // Header
    ctx.fillStyle = hasAlarms ? screens.alarm.accent : screens.alarm.safe;
    ctx.font = 'bold 12px "SF Pro Display", -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(hasAlarms ? 'ACTIVE ALARMS' : 'ALL SYSTEMS OK', x, y - 122);

    if (hasAlarms) {
      activeAlarms.slice(0, 3).forEach((kpi, i) => {
        const alarmY = y - 100 + i * 32;

        // Alarm background (pulsing)
        ctx.fillStyle = `rgba(255, 59, 48, ${0.15 + pulse * 0.15})`;
        roundRect(ctx, x - 74, alarmY - 4, 148, 26, 4);
        ctx.fill();

        // Warning icon
        ctx.font = '14px sans-serif';
        ctx.fillText('⚠️', x - 62, alarmY + 10);

        // Alarm name
        ctx.fillStyle = '#FECACA';
        ctx.font = 'bold 10px "SF Pro Text", -apple-system, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(kpi.name, x - 44, alarmY + 6);

        // Alarm detail
        ctx.fillStyle = '#FCA5A5';
        ctx.font = '9px "SF Pro Text", -apple-system, sans-serif';
        ctx.fillText(`${kpi.value}/${kpi.target} - ${kpi.status}`, x - 44, alarmY + 18);
      });
    } else {
      // All OK display
      ctx.fillStyle = screens.alarm.safe;
      ctx.font = '32px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('✓', x, y - 75);

      ctx.fillStyle = '#6EE7B7';
      ctx.font = '11px "SF Pro Text", -apple-system, sans-serif';
      ctx.fillText('All KPIs within thresholds', x, y - 50);
      ctx.fillText('System operating normally', x, y - 36);
    }
  }, [ORIGIN_X, ORIGIN_Y]);

  // Draw Recommendations screen
  const drawRecommendScreen = useCallback((ctx: CanvasRenderingContext2D, col: number, row: number) => {
    const { x, y } = isoToScreen(col, row, ORIGIN_X, ORIGIN_Y);
    const { screens } = CARTOON_COLORS;

    // Monitor stand
    ctx.fillStyle = screens.bezel;
    ctx.fillRect(x - 6, y - 25, 12, 28);

    // Monitor base
    ctx.fillStyle = screens.frame;
    ctx.beginPath();
    ctx.ellipse(x, y + 6, 28, 10, 0, 0, Math.PI * 2);
    ctx.fill();

    // Monitor frame
    applySoftShadow(ctx, 4, 4, 10, CARTOON_COLORS.shadows.medium);
    ctx.fillStyle = screens.frame;
    roundRect(ctx, x - 88, y - 148, 176, 125, 8);
    ctx.fill();
    clearShadow(ctx);

    // Screen bezel
    ctx.fillStyle = screens.frameDark;
    roundRect(ctx, x - 84, y - 144, 168, 117, 6);
    ctx.fill();

    // Screen
    ctx.fillStyle = screens.screen;
    roundRect(ctx, x - 80, y - 140, 160, 109, 4);
    ctx.fill();

    // Screen border glow
    ctx.strokeStyle = screens.recommend.accent;
    ctx.lineWidth = 2;
    roundRect(ctx, x - 80, y - 140, 160, 109, 4);
    ctx.stroke();

    // Header
    ctx.fillStyle = screens.recommend.accent;
    ctx.font = 'bold 12px "SF Pro Display", -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('RECOMMENDATIONS', x, y - 122);

    // Sample recommendations
    const recommendations = [
      { icon: '🎯', text: 'Apply to 3 more jobs today' },
      { icon: '📚', text: 'Complete React module' },
      { icon: '🤝', text: 'Connect with 2 recruiters' },
    ];

    recommendations.forEach((rec, i) => {
      const recY = y - 98 + i * 30;

      // Recommendation background
      ctx.fillStyle = 'rgba(175, 82, 222, 0.1)';
      roundRect(ctx, x - 74, recY - 4, 148, 24, 4);
      ctx.fill();

      // Icon
      ctx.font = '13px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(rec.icon, x - 68, recY + 12);

      // Text
      ctx.fillStyle = screens.recommend.accentLight;
      ctx.font = '10px "SF Pro Text", -apple-system, sans-serif';
      ctx.fillText(rec.text, x - 48, recY + 12);
    });

    // Priority badge
    ctx.fillStyle = screens.recommend.accent;
    ctx.beginPath();
    ctx.arc(x + 65, y - 120, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 10px "SF Pro Display", -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('3', x + 65, y - 117);
  }, [ORIGIN_X, ORIGIN_Y]);

  // Draw cartoon-style agent
  const drawAgent = useCallback((ctx: CanvasRenderingContext2D, agent: IsoAgent, animFrame: number) => {
    const { x, y } = isoToScreen(agent.position.x, agent.position.y, ORIGIN_X, ORIGIN_Y);
    const { agents } = CARTOON_COLORS;

    // Animation offsets
    const bobOffset = agent.activity === 'WALKING' ? Math.sin(animFrame * ANIMATION_CONFIG.agent.bobFrequency) * ANIMATION_CONFIG.agent.bobAmplitude : 0;
    const legOffset = agent.activity === 'WALKING' ? Math.sin(animFrame * ANIMATION_CONFIG.agent.legSwingFrequency) * ANIMATION_CONFIG.agent.legSwingAmplitude : 0;

    // Get agent colors based on role
    const roleColors = agent.role === 'COORDINATOR' ? agents.coordinator
      : agent.role === 'DELEGATOR' ? agents.delegator
      : agents.worker;

    // Outer glow for visibility
    ctx.beginPath();
    ctx.arc(x, y - 32 + bobOffset, 38, 0, Math.PI * 2);
    ctx.fillStyle = roleColors.glow;
    ctx.fill();

    // Shadow
    ctx.fillStyle = CARTOON_COLORS.shadows.medium;
    ctx.beginPath();
    ctx.ellipse(x, y + 8, 20, 8, 0, 0, Math.PI * 2);
    ctx.fill();

    // Legs
    ctx.fillStyle = '#374151';
    ctx.fillRect(x - 9 - legOffset, y - 14 + bobOffset, 7, 20);
    ctx.fillRect(x + 2 + legOffset, y - 14 + bobOffset, 7, 20);

    // Shoes
    ctx.fillStyle = '#1F2937';
    ctx.beginPath();
    ctx.ellipse(x - 5.5 - legOffset, y + 7, 7, 4, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(x + 5.5 + legOffset, y + 7, 7, 4, 0, 0, Math.PI * 2);
    ctx.fill();

    // Body with gradient
    const bodyGradient = ctx.createRadialGradient(
      x - 5, y - 35 + bobOffset, 0,
      x, y - 30 + bobOffset, 22
    );
    bodyGradient.addColorStop(0, roleColors.primary);
    bodyGradient.addColorStop(1, roleColors.secondary);
    ctx.fillStyle = bodyGradient;
    ctx.beginPath();
    ctx.ellipse(x, y - 32 + bobOffset, 18, 22, 0, 0, Math.PI * 2);
    ctx.fill();

    // Body outline
    ctx.strokeStyle = roleColors.secondary;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Head
    const headGradient = ctx.createRadialGradient(
      x - 4, y - 60 + bobOffset, 0,
      x, y - 56 + bobOffset, 16
    );
    headGradient.addColorStop(0, agents.skin);
    headGradient.addColorStop(1, agents.skinShadow);
    ctx.fillStyle = headGradient;
    ctx.beginPath();
    ctx.arc(x, y - 58 + bobOffset, 16, 0, Math.PI * 2);
    ctx.fill();

    // Hair
    const hairColor = agent.role === 'COORDINATOR' ? agents.hair.dark
      : agent.role === 'DELEGATOR' ? agents.hair.brown
      : agents.hair.blonde;
    ctx.fillStyle = hairColor;
    ctx.beginPath();
    ctx.arc(x, y - 64 + bobOffset, 14, Math.PI, Math.PI * 2);
    ctx.fill();

    // Eyes
    ctx.fillStyle = '#1F2937';
    ctx.beginPath();
    ctx.arc(x - 5, y - 58 + bobOffset, 2.5, 0, Math.PI * 2);
    ctx.arc(x + 5, y - 58 + bobOffset, 2.5, 0, Math.PI * 2);
    ctx.fill();

    // Eye highlights
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.arc(x - 4, y - 59 + bobOffset, 1, 0, Math.PI * 2);
    ctx.arc(x + 6, y - 59 + bobOffset, 1, 0, Math.PI * 2);
    ctx.fill();

    // Smile
    ctx.strokeStyle = '#92400E';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(x, y - 53 + bobOffset, 5, 0.1 * Math.PI, 0.9 * Math.PI);
    ctx.stroke();

    // Role badge
    ctx.fillStyle = roleColors.accent;
    ctx.beginPath();
    ctx.arc(x, y - 32 + bobOffset, 9, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = roleColors.secondary;
    ctx.font = 'bold 11px "SF Pro Display", -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(agent.role.charAt(0), x, y - 31 + bobOffset);

    // Activity bubble
    if (agent.activity !== 'IDLE' && agent.activity !== 'WALKING') {
      const bubbleY = y - 85 + bobOffset;

      // Bubble
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.beginPath();
      ctx.arc(x, bubbleY, 18, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = roleColors.primary;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Activity icon
      const icons: Record<string, string> = {
        WORKING: '💻',
        TYPING: '⌨️',
        THINKING: '💡',
        TALKING: '💬',
        DRINKING_COFFEE: '☕',
        MEETING: '👥',
        PRESENTING: '📊',
      };
      ctx.font = '18px sans-serif';
      ctx.textBaseline = 'middle';
      ctx.fillText(icons[agent.activity] || '⏳', x, bubbleY + 1);
    }

    // Name label
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    const nameWidth = ctx.measureText(agent.name).width + 12;
    roundRect(ctx, x - nameWidth / 2, y + 14, nameWidth, 18, 4);
    ctx.fill();
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 11px "SF Pro Text", -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(agent.name, x, y + 23);

    // Progress bar
    if (agent.activityProgress !== undefined && agent.activityProgress > 0) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      roundRect(ctx, x - 28, y + 36, 56, 8, 3);
      ctx.fill();
      ctx.fillStyle = roleColors.primary;
      roundRect(ctx, x - 28, y + 36, (agent.activityProgress / 100) * 56, 8, 3);
      ctx.fill();
    }
  }, [ORIGIN_X, ORIGIN_Y]);

  // Apply time-of-day lighting overlay
  const applyLighting = useCallback((ctx: CanvasRenderingContext2D) => {
    if (timeLighting.tintOpacity > 0) {
      ctx.fillStyle = timeLighting.ambientColor;
      ctx.fillRect(0, CARTOON_CANVAS.HEADER_HEIGHT, WIDTH, HEIGHT - CARTOON_CANVAS.HEADER_HEIGHT);
    }
  }, [timeLighting, WIDTH, HEIGHT]);

  // ===========================================
  // MAIN RENDER FUNCTION
  // ===========================================

  const render = useCallback((timestamp: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const animFrame = Math.floor(timestamp / 100);

    // Clear canvas
    ctx.clearRect(0, 0, WIDTH, HEIGHT);

    // 1. Draw background/sky
    drawBackground(ctx);

    // 2. Draw floor tiles
    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        const isCarpet = col >= 10 && col <= 14 && row >= 8 && row <= 12;
        drawTile(ctx, col, row, isCarpet);
      }
    }

    // 3. Collect all objects for depth sorting
    interface Renderable {
      type: 'furniture' | 'agent';
      depth: number;
      render: () => void;
    }

    const renderObjects: Renderable[] = [];

    // Add furniture
    state.layout.furniture.forEach((furniture) => {
      const depth = calculateDepth(furniture.position.x, furniture.position.y);
      renderObjects.push({
        type: 'furniture',
        depth,
        render: () => {
          switch (furniture.type) {
            case 'DESK_MODERN':
              drawDesk(ctx, furniture.position.x, furniture.position.y);
              break;
            case 'CHAIR_OFFICE':
              drawChair(ctx, furniture.position.x, furniture.position.y);
              break;
            case 'PLANT_LARGE':
              drawPlant(ctx, furniture.position.x, furniture.position.y, true);
              break;
            case 'PLANT_SMALL':
              drawPlant(ctx, furniture.position.x, furniture.position.y, false);
              break;
            case 'COFFEE_MACHINE':
              drawCoffeeMachine(ctx, furniture.position.x, furniture.position.y);
              break;
            case 'TABLE_MEETING':
              drawMeetingTable(ctx, furniture.position.x, furniture.position.y);
              break;
            case 'SCREEN_KPI':
              drawKPIScreen(ctx, furniture.position.x, furniture.position.y, state.kpis);
              break;
            case 'SCREEN_ALARM':
              drawAlarmScreen(ctx, furniture.position.x, furniture.position.y, state.kpis, animFrame);
              break;
            case 'SCREEN_RECOMMEND':
              drawRecommendScreen(ctx, furniture.position.x, furniture.position.y);
              break;
          }
        },
      });
    });

    // Add agents
    state.agents.forEach((agent) => {
      const depth = calculateDepth(agent.position.x, agent.position.y, 1);
      renderObjects.push({
        type: 'agent',
        depth,
        render: () => drawAgent(ctx, agent, animFrame),
      });
    });

    // 4. Sort and render furniture first
    const furnitureObjects = renderObjects.filter(obj => obj.type === 'furniture');
    const agentObjects = renderObjects.filter(obj => obj.type === 'agent');

    furnitureObjects.sort((a, b) => a.depth - b.depth);
    furnitureObjects.forEach((obj) => obj.render());

    // 5. Apply lighting overlay
    applyLighting(ctx);

    // 6. Render agents on top
    agentObjects.sort((a, b) => a.depth - b.depth);
    agentObjects.forEach((obj) => obj.render());

    // 7. Draw header overlay (always on top)
    drawHeaderOverlay(ctx, WIDTH, 'Mundus Novus', mundusName, sentiment, state.currentTime);

    // 8. Draw status indicator (bottom right)
    const statusY = HEIGHT - 40;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    roundRect(ctx, WIDTH - 180, statusY, 170, 32, 8);
    ctx.fill();

    ctx.fillStyle = '#FFFFFF';
    ctx.font = '12px "JetBrains Mono", monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      state.currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      WIDTH - 170,
      statusY + 16
    );

    ctx.fillText(`${state.speed}x`, WIDTH - 100, statusY + 16);

    // Status dot
    ctx.fillStyle = state.isRunning
      ? state.isPaused ? '#FF9500' : '#34C759'
      : '#8E8E93';
    ctx.beginPath();
    ctx.arc(WIDTH - 30, statusY + 16, 6, 0, Math.PI * 2);
    ctx.fill();

  }, [
    state,
    sentiment,
    mundusName,
    WIDTH,
    HEIGHT,
    GRID_ROWS,
    GRID_COLS,
    drawBackground,
    drawTile,
    drawDesk,
    drawChair,
    drawPlant,
    drawCoffeeMachine,
    drawMeetingTable,
    drawKPIScreen,
    drawAlarmScreen,
    drawRecommendScreen,
    drawAgent,
    applyLighting,
  ]);

  // ===========================================
  // ANIMATION LOOP
  // ===========================================

  useEffect(() => {
    let animating = true;
    let slowModeTimeout: ReturnType<typeof setTimeout> | null = null;

    const animate = (timestamp: number) => {
      if (!animating) return;

      render(timestamp);

      if (state.isRunning && !state.isPaused) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        slowModeTimeout = setTimeout(() => {
          if (animating) {
            animationFrameRef.current = requestAnimationFrame(animate);
          }
        }, 500);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      animating = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (slowModeTimeout) {
        clearTimeout(slowModeTimeout);
      }
    };
  }, [state.isRunning, state.isPaused, render]);

  // Force re-render when agents change
  useEffect(() => {
    if (canvasRef.current) {
      render(performance.now());
    }
  }, [state.agents, render]);

  return (
    <div className={`relative ${className}`}>
      <canvas
        ref={canvasRef}
        width={WIDTH}
        height={HEIGHT}
        className="rounded-2xl shadow-2xl"
        style={{
          maxWidth: '100%',
          height: 'auto',
          aspectRatio: '1 / 1',
        }}
      />

      {/* Alarm overlay indicator */}
      {state.activeAlarms.length > 0 && (
        <div className="absolute bottom-16 right-4 bg-red-500/90 text-white px-4 py-2 rounded-lg animate-pulse shadow-lg">
          <span className="font-semibold">{state.activeAlarms.length} Active Alarm(s)</span>
        </div>
      )}
    </div>
  );
};

export default CartoonCanvas;
